import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useSettings } from "../lib/settings";
import { useTheme } from "../lib/theme";
import { useLocation, useNavigate } from "react-router-dom";
import { RefreshCw, Sparkles, Save, Sun, Moon, Upload, Image as ImageIcon, RotateCcw, Mail, CheckCircle2, AlertCircle, Plus, Trash2 } from "lucide-react";
import { PROJECT_STATUSES, STATUS_CAPTIONS } from "../lib/constants";
import { InternalUsersTab, AccountTab } from "./AdminUserSettings";
import AdminPortalUsers from "./AdminPortalUsers";

const inputCls =
  "w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3.5 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)] focus:ring-1 focus:ring-[color:var(--atech-copper)]";

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="label block mb-2">{label}</span>
    {children}
    {hint && <span className="block mt-2 text-[11px] text-[color:var(--atech-text-muted)]">{hint}</span>}
  </label>
);

export default function AdminSettings() {
  const { settings, refresh } = useSettings();
  const { theme, setTheme } = useTheme();
  const [form, setForm] = useState(settings || {});
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginOk, setLoginOk] = useState(false);
  const [err, setErr] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [demoMsg, setDemoMsg] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    const allowed = ["general", "login_page", "lists", "status_descriptions", "users", "portal_users", "account"];
    if (t && allowed.includes(t)) setActiveTab(t);
  }, [location.search]);

  // Status captions form state
  const [captionsForm, setCaptionsForm] = useState({});
  const [captionsBusy, setCaptionsBusy] = useState(false);
  const [captionsOk, setCaptionsOk] = useState(false);
  const [captionsErr, setCaptionsErr] = useState("");

  // Lists form state
  const [listsForm, setListsForm] = useState({ project_types: [], contact_roles: [] });
  const [listsBusy, setListsBusy] = useState(false);
  const [listsOk, setListsOk] = useState(false);
  const [listsErr, setListsErr] = useState("");

  useEffect(() => {
    setForm(settings || {});
    setCaptionsForm(settings?.status_captions || {});
    setListsForm({
      project_types: settings?.project_types || [],
      contact_roles: settings?.contact_roles || [],
    });
  }, [settings]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Save General fields (practice name, portal name, logo url)
  const saveGeneral = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk(false);
    try {
      await api.patch("/settings", {
        practice_name: form.practice_name,
        portal_name: form.portal_name,
        logo_url: form.logo_url,
      });
      await refresh();
      setOk(true); setTimeout(() => setOk(false), 2500);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  // Save Login page fields
  const saveLoginPage = async (e) => {
    e.preventDefault();
    setLoginBusy(true); setLoginErr(""); setLoginOk(false);
    try {
      await api.patch("/settings", {
        login_eyebrow: form.login_eyebrow,
        login_headline: form.login_headline,
        login_body: form.login_body,
        login_footer: form.login_footer,
        login_side_caption: form.login_side_caption,
        login_gdpr: form.login_gdpr ?? "",
        default_theme: form.default_theme,
      });
      await refresh();
      setLoginOk(true); setTimeout(() => setLoginOk(false), 2500);
    } catch (e2) {
      setLoginErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoginBusy(false); }
  };

  const reset = async () => {
    if (!confirm("Remove all demo data tagged is_demo=true? Your real data is kept.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo/reset");
      setDemoMsg(`Removed: ${Object.entries(data).map(([k, v]) => `${k}=${v}`).join(", ")}`);
    } finally { setBusy(false); }
  };
  const seed = async () => {
    setBusy(true);
    try { await api.post("/admin/demo/seed"); setDemoMsg("Demo data seeded (if it was missing)."); }
    finally { setBusy(false); }
  };

  const saveCaptions = async (e) => {
    e.preventDefault();
    setCaptionsBusy(true);
    try {
      await api.patch("/settings", { status_captions: captionsForm });
      await refresh();
      setCaptionsOk(true);
      setTimeout(() => setCaptionsOk(false), 2500);
    } catch (e) { setCaptionsErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setCaptionsBusy(false); }
  };

  const saveLists = async (e) => {
    e.preventDefault();
    setListsBusy(true);
    try {
      await api.patch("/settings", {
        project_types: listsForm.project_types,
        contact_roles: listsForm.contact_roles,
      });
      await refresh();
      setListsOk(true);
      setTimeout(() => setListsOk(false), 2500);
    } catch (e) { setListsErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setListsBusy(false); }
  };

  const TABS = [
    { key: "general",             label: "General" },
    { key: "login_page",          label: "Login page" },
    { key: "lists",               label: "Lists" },
    { key: "status_descriptions", label: "Status descriptions" },
    { key: "users",               label: "Internal users" },
    { key: "portal_users", label: "Portal users" },
    { key: "account",             label: "Account" },
  ];

  const switchTab = (key) => {
    setActiveTab(key);
    navigate(`/admin/settings?tab=${key}`, { replace: true });
  };

  return (
    <div className="max-w-6xl mx-auto px-8 sm:px-12 py-12" data-testid="admin-settings-page">
      <div className="label text-[color:var(--atech-copper)] mb-2">Practice</div>
      <h1 className="font-display text-4xl tracking-tight text-[color:var(--atech-text)] mb-2">Settings</h1>
      <p className="text-sm text-[color:var(--atech-text-dim)] mb-8 max-w-2xl">
        Manage branding, login page content, configurable lists, and team accounts.
      </p>

      {/* Tab bar */}
      <div className="flex gap-1 mb-8 border-b border-white/8 -mx-1 px-1 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            className={`px-4 py-2.5 text-xs font-medium uppercase tracking-[0.14em] transition-colors border-b-[2px] -mb-px whitespace-nowrap ${
              activeTab === t.key
                ? "border-[color:var(--atech-copper)] text-[color:var(--atech-text)]"
                : "border-transparent text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-text)]"
            }`}
            data-testid={`settings-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── General ─────────────────────────────────────────────────── */}
      {activeTab === "general" && (
        <>
          <LogoUploader settings={settings} onUpdated={refresh} />
          <EmailDeliveryCard email={settings?.email} />

          <form onSubmit={saveGeneral} className="card-surface p-8 mb-6 space-y-5" data-testid="general-form">
            <div>
              <div className="label text-[color:var(--atech-copper)] mb-1">Branding</div>
              <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Practice details</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Practice name">
                <input value={form.practice_name || ""} onChange={(e) => update("practice_name", e.target.value)} className={inputCls} data-testid="settings-practice-name" />
              </Field>
              <Field label="Portal name">
                <input value={form.portal_name || ""} onChange={(e) => update("portal_name", e.target.value)} className={inputCls} data-testid="settings-portal-name" />
              </Field>
            </div>
            <Field label="Logo URL (advanced)" hint="Set automatically when you upload a logo above. Paste an external URL here if preferred.">
              <input value={form.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} className={inputCls} data-testid="settings-logo-url" />
            </Field>
            {err && <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2">{err}</div>}
            <div className="flex items-center justify-between pt-2">
              {ok && <span className="text-xs text-[#34D399]">Saved</span>}
              <button type="submit" disabled={busy} className="btn-copper text-sm flex items-center gap-2 ml-auto" data-testid="save-settings-button">
                <Save size={14} /> {busy ? "Saving…" : "Save changes"}
              </button>
            </div>
          </form>

          {/* Demo data */}
          <div className="card-surface p-8 mb-6">
            <div className="label mb-1">Demo data</div>
            <p className="text-sm text-[color:var(--atech-text-dim)] mb-5">
              All demo records are tagged <span className="font-mono text-[color:var(--atech-text)]">is_demo=true</span> so they can
              be wiped cleanly before go-live.
            </p>
            <div className="flex gap-3">
              <button onClick={seed} disabled={busy} className="text-sm px-4 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)] flex items-center gap-2" data-testid="seed-demo-button">
                <Sparkles size={14} /> Seed demo data
              </button>
              <button onClick={reset} disabled={busy} className="text-sm px-4 py-2.5 rounded-[2px] border border-[rgba(220,38,38,0.3)] text-[#F87171] hover:bg-[rgba(220,38,38,0.08)] flex items-center gap-2" data-testid="reset-demo-button">
                <RefreshCw size={14} /> Remove demo data
              </button>
            </div>
            {demoMsg && <div className="mt-4 text-xs text-[color:var(--atech-text-dim)]">{demoMsg}</div>}
          </div>

          <div className="card-surface p-8 opacity-80">
            <div className="label mb-1">Phase 2 roadmap</div>
            <ul className="text-sm text-[color:var(--atech-text-dim)] space-y-2 mt-3 list-disc list-inside">
              <li>Internal project management + task tracking</li>
              <li>Timesheets, fixed-fee vs time-charge</li>
              <li>Fee stages, profitability, invoice prompts</li>
              <li>Xero export / integration</li>
              <li>Practice dashboard + AI insights</li>
              <li>Consultant / third-party collaboration</li>
            </ul>
          </div>
        </>
      )}

      {/* ── Login page ──────────────────────────────────────────────── */}
      {activeTab === "login_page" && (
        <form onSubmit={saveLoginPage} className="card-surface p-8 space-y-6" data-testid="login-content-form">
          <div>
            <div className="label text-[color:var(--atech-copper)] mb-1">Branding · Login page</div>
            <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Login page content</h2>
            <p className="text-sm text-[color:var(--atech-text-dim)] mt-2">
              These fields control what clients see on the public login page. Changes apply immediately.
            </p>
          </div>

          <Field label="Eyebrow / strapline (above the headline)">
            <input value={form.login_eyebrow || ""} onChange={(e) => update("login_eyebrow", e.target.value)} className={inputCls} data-testid="settings-login-eyebrow" />
          </Field>
          <Field label="Headline">
            <input value={form.login_headline || ""} onChange={(e) => update("login_headline", e.target.value)} className={inputCls} data-testid="settings-login-headline" />
          </Field>
          <Field label="Body copy" hint="Shown under the headline on the login page.">
            <textarea rows={5} value={form.login_body || ""} onChange={(e) => update("login_body", e.target.value)} className={`${inputCls} resize-y`} data-testid="settings-login-body" />
          </Field>
          <Field label="Side-panel caption" hint="Short line rendered over the architectural hero image.">
            <input value={form.login_side_caption || ""} onChange={(e) => update("login_side_caption", e.target.value)} className={inputCls} data-testid="settings-login-side-caption" />
          </Field>
          <Field label="Footer / help text (optional)">
            <textarea rows={2} value={form.login_footer || ""} onChange={(e) => update("login_footer", e.target.value)} className={`${inputCls} resize-none`} data-testid="settings-login-footer" />
          </Field>
          <Field label="Security &amp; GDPR notice (optional)" hint="Shown below the sign-in button under a 'Security &amp; Privacy' label. Leave blank to hide it.">
            <textarea rows={3} value={form.login_gdpr ?? ""} onChange={(e) => update("login_gdpr", e.target.value)} className={`${inputCls} resize-y`} placeholder="e.g. Your documents are encrypted in transit and at rest…" data-testid="settings-login-gdpr" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-end">
            <Field label="Default theme for new visitors" hint="Users can override this on the login page; their choice is remembered.">
              <select value={form.default_theme || "dark"} onChange={(e) => update("default_theme", e.target.value)} className={inputCls} data-testid="settings-default-theme">
                <option value="dark">Dark</option>
                <option value="light">Light</option>
              </select>
            </Field>
            <div className="flex items-center gap-3">
              <span className="label">Preview my theme:</span>
              <button type="button" onClick={() => setTheme("dark")} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-[2px] border ${theme === "dark" ? "border-[color:var(--atech-copper)] text-[color:var(--atech-copper)]" : "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)]"}`} data-testid="preview-dark">
                <Moon size={12} /> Dark
              </button>
              <button type="button" onClick={() => setTheme("light")} className={`flex items-center gap-2 text-xs px-3 py-2 rounded-[2px] border ${theme === "light" ? "border-[color:var(--atech-copper)] text-[color:var(--atech-copper)]" : "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)]"}`} data-testid="preview-light">
                <Sun size={12} /> Light
              </button>
            </div>
          </div>

          {loginErr && <div className="text-xs text-[#F87171] border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.08)] rounded-[2px] px-3 py-2">{loginErr}</div>}
          <div className="flex items-center justify-between pt-2">
            {loginOk && <span className="text-xs text-[#34D399]">Saved — refresh the login page to see it live.</span>}
            <button type="submit" disabled={loginBusy} className="btn-copper text-sm flex items-center gap-2 ml-auto" data-testid="save-login-page-button">
              <Save size={14} /> {loginBusy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      )}

      {/* ── Lists ───────────────────────────────────────────────────── */}
      {activeTab === "lists" && (
        <form onSubmit={saveLists} className="space-y-6" data-testid="lists-form">
          <div className="card-surface p-8">
            <div className="label text-[color:var(--atech-copper)] mb-1">Configurable lists</div>
            <h2 className="font-display text-2xl text-[color:var(--atech-text)] mb-1">Project types &amp; contact roles</h2>
            <p className="text-sm text-[color:var(--atech-text-dim)] mt-2 mb-8 max-w-2xl">
              Add or remove items from these lists. Changes take effect in the dropdowns immediately after saving.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <ListEditor
                label="Project types"
                hint="Shown in the Type dropdown when creating or editing a project."
                items={listsForm.project_types}
                onChange={(v) => setListsForm((f) => ({ ...f, project_types: v }))}
                testPrefix="project-types"
              />
              <ListEditor
                label="Contact roles"
                hint="Shown in the Role dropdown when adding a team member to a project."
                items={listsForm.contact_roles}
                onChange={(v) => setListsForm((f) => ({ ...f, contact_roles: v }))}
                testPrefix="contact-roles"
              />
            </div>

            <div className="flex items-center gap-3 mt-8 pt-6 border-t border-[color:var(--atech-border)]">
              <button type="submit" disabled={listsBusy} className="btn-copper text-sm flex items-center gap-2" data-testid="save-lists-button">
                <Save size={14} /> {listsBusy ? "Saving…" : "Save lists"}
              </button>
              {listsOk && (
                <span className="flex items-center gap-1.5 text-xs text-[#34D399]" data-testid="lists-saved">
                  <CheckCircle2 size={13} /> Saved
                </span>
              )}
              {listsErr && (
                <span className="text-xs text-[#F87171]" data-testid="lists-error">{listsErr}</span>
              )}
            </div>
          </div>
        </form>
      )}

      {activeTab === "status_descriptions" && (
        <StatusCaptionsEditor
          captions={captionsForm}
          onChange={setCaptionsForm}
          onSave={saveCaptions}
          busy={captionsBusy}
          ok={captionsOk}
          err={captionsErr}
        />
      )}
      {activeTab === "users" && <InternalUsersTab />}
      {activeTab === "portal_users" && <AdminPortalUsers />}
      {activeTab === "account" && <AccountTab />}
    </div>
  );
}


/* ── ListEditor — reusable add/remove list component ──────────────── */
function ListEditor({ label, hint, items, onChange, testPrefix }) {
  const [newItem, setNewItem] = useState("");
  const add = () => {
    const v = newItem.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setNewItem("");
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const handleKey = (e) => { if (e.key === "Enter") { e.preventDefault(); add(); } };
  return (
    <div>
      <div className="label mb-1">{label}</div>
      {hint && <p className="text-[11px] text-[color:var(--atech-text-muted)] mb-4 leading-relaxed">{hint}</p>}
      <ul className="space-y-1 mb-3" data-testid={`${testPrefix}-list`}>
        {items.map((item, i) => (
          <li key={`${item}-${i}`} className="flex items-center justify-between gap-2 px-3 py-2 rounded-[2px] bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] group">
            <span className="text-sm text-[color:var(--atech-text)]">{item}</span>
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-[color:var(--atech-text-muted)] hover:text-[#F87171] opacity-0 group-hover:opacity-100 transition-all"
              data-testid={`${testPrefix}-remove-${i}`}
            >
              <Trash2 size={12} />
            </button>
          </li>
        ))}
        {items.length === 0 && (
          <li className="text-xs text-[color:var(--atech-text-muted)] px-3 py-2">No items yet. Add one below.</li>
        )}
      </ul>
      <div className="flex gap-2">
        <input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Add new item…"
          className={`${inputCls} flex-1`}
          data-testid={`${testPrefix}-new-input`}
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 text-xs rounded-[2px] border border-[color:var(--atech-copper)] text-[color:var(--atech-copper)] hover:bg-[color:var(--atech-copper)] hover:text-white transition-colors flex items-center gap-1.5"
          data-testid={`${testPrefix}-add-button`}
        >
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}


function LogoUploader({ settings, onUpdated }) {
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [preview, setPreview] = useState(null);

  const currentLogo = settings?.logo_url || "/atech-logo.png";
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const resolvedLogo = currentLogo.startsWith("/api/") ? `${BACKEND_URL}${currentLogo}` : currentLogo;
  const isCustom = !!settings?.logo_storage_path;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return setErr("Logo must be 2 MB or smaller.");
    const ok_mimes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!ok_mimes.includes(file.type)) return setErr("Unsupported file type. Use PNG, JPG, WEBP, or SVG.");
    setBusy(true); setErr(""); setOk("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await onUpdated();
      setOk("Logo updated across the portal.");
      setTimeout(() => setOk(""), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); setPreview(null); }
  };

  const reset = async () => {
    if (!confirm("Reset to the default A-Tech logo?")) return;
    setBusy(true); setErr(""); setOk("");
    try {
      await api.delete("/settings/logo");
      await onUpdated();
      setOk("Restored the default logo.");
      setTimeout(() => setOk(""), 2500);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer?.files?.[0];
    if (f) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target.result);
      reader.readAsDataURL(f);
      upload(f);
    }
  };

  return (
    <div className="card-surface p-8 mb-6" data-testid="logo-uploader">
      <div className="label text-[color:var(--atech-copper)] mb-1">Branding · Logo</div>
      <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Portal logo</h2>
      <p className="text-sm text-[color:var(--atech-text-dim)] mt-2 mb-6 max-w-2xl">
        Upload your practice logo. It will update immediately on the login
        page and portal header, in both dark and light themes. Recommended:
        PNG with a transparent background.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-start">
        {/* Current logo preview — rendered on both dark and light chips */}
        <div className="space-y-3">
          <div className="label">Current</div>
          <div className="flex flex-col gap-2">
            <div className="h-24 flex items-center justify-center rounded-[2px] border border-[color:var(--atech-border)] bg-[#0c0c0e] p-3">
              <img src={resolvedLogo} alt="Logo on dark" className="max-h-full max-w-full object-contain" data-testid="logo-preview-dark" />
            </div>
            <div className="h-24 flex items-center justify-center rounded-[2px] border border-[color:var(--atech-border)] bg-[#f5f1ea] p-3">
              <img src={resolvedLogo} alt="Logo on light" className="max-h-full max-w-full object-contain" data-testid="logo-preview-light" />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)]">
            {isCustom ? "Uploaded logo" : "Default A-Tech logo"}
          </div>
        </div>

        {/* Drop zone */}
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={onDrop}
            onClick={() => fileInput.current?.click()}
            className="border border-dashed border-[color:var(--atech-border)] rounded-[2px] p-8 text-center cursor-pointer hover:border-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper)] transition-colors"
            data-testid="logo-dropzone"
          >
            <ImageIcon size={28} className="mx-auto text-[color:var(--atech-text-muted)] mb-3" strokeWidth={1.25} />
            <div className="font-display text-lg text-[color:var(--atech-text)]">Upload a new logo</div>
            <div className="text-xs text-[color:var(--atech-text-dim)] mt-1">
              Drop a file here or click to browse · PNG, JPG, WEBP, SVG · max 2&nbsp;MB
            </div>
            <input
              ref={fileInput}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="logo-file-input"
            />
          </div>
          <div className="flex gap-3 mt-4">
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
              className="text-sm px-4 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)] flex items-center gap-2"
              data-testid="logo-replace"
            >
              <Upload size={14} /> {busy ? "Uploading…" : "Choose file"}
            </button>
            {isCustom && (
              <button
                type="button"
                onClick={reset}
                disabled={busy}
                className="text-sm px-4 py-2.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-text)] hover:border-[color:var(--atech-border-strong)] flex items-center gap-2"
                data-testid="logo-reset"
              >
                <RotateCcw size={14} /> Reset to default
              </button>
            )}
          </div>
          {err && <div className="mt-3 text-xs text-[#F87171]">{err}</div>}
          {ok && <div className="mt-3 text-xs text-[#34D399]">{ok}</div>}
        </div>
      </div>
    </div>
  );
}


function EmailDeliveryCard({ email }) {
  const enabled = !!email?.enabled;
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null); // { ok, message }

  const sendTest = async () => {
    setSending(true); setResult(null);
    try {
      const { data } = await api.post("/settings/test-email", {});
      setResult({ ok: true, message: `Sent to ${data.to}. Check your inbox (and spam folder on first send).` });
    } catch (e) {
      setResult({ ok: false, message: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="card-surface p-8 mb-6" data-testid="email-delivery-card">
      <div className="flex items-start justify-between gap-6">
        <div className="flex-1">
          <div className="label text-[color:var(--atech-copper)] mb-1 flex items-center gap-2">
            <Mail size={12} /> Email delivery
          </div>
          <h2 className="font-display text-2xl text-[color:var(--atech-text)]">Invite emails</h2>
          <p className="text-sm text-[color:var(--atech-text-dim)] mt-2 max-w-2xl">
            Controls whether activation-invite emails are sent automatically
            when you (or a client admin) click <span className="text-[color:var(--atech-text)]">Send invite</span>.
            The manual "Copy link" flow always works regardless.
          </p>
        </div>
        {enabled ? (
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-[2px] border border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.08)] text-[#34D399] shrink-0" data-testid="email-status-enabled">
            <CheckCircle2 size={12} /> Enabled
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] shrink-0" data-testid="email-status-disabled">
            <AlertCircle size={12} /> Not configured
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6 text-xs">
        <div>
          <div className="label mb-1">Provider</div>
          <div className="text-[color:var(--atech-text)]">{enabled ? "Resend" : "None (no-op)"}</div>
        </div>
        <div>
          <div className="label mb-1">Sender</div>
          <div className="text-[color:var(--atech-text)] font-mono text-[11px] break-all">
            {email?.sender || <span className="text-[color:var(--atech-text-muted)]">— not set —</span>}
          </div>
        </div>
      </div>

      {enabled && (
        <div className="mt-6 border-t border-[color:var(--atech-border)] pt-5 flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="label mb-1">Verify delivery</div>
            <div className="text-xs text-[color:var(--atech-text-dim)] leading-relaxed">
              Send a test message to your own account (<span className="font-mono text-[color:var(--atech-text)]">your admin email</span>) to confirm DNS records have propagated.
            </div>
          </div>
          <button
            onClick={sendTest}
            disabled={sending}
            className="btn-copper text-sm flex items-center gap-2 disabled:opacity-50"
            data-testid="send-test-email-button"
          >
            <Mail size={13} /> {sending ? "Sending…" : "Send test email"}
          </button>
        </div>
      )}
      {result && (
        <div
          className={`mt-4 text-xs px-3 py-2 rounded-[2px] border ${
            result.ok
              ? "border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.06)] text-[#34D399]"
              : "border-[rgba(220,38,38,0.3)] bg-[rgba(220,38,38,0.06)] text-[#F87171]"
          }`}
          data-testid="test-email-result"
        >
          {result.message}
        </div>
      )}

      {!enabled && (
        <div className="mt-6 border-t border-[color:var(--atech-border)] pt-5">
          <div className="label mb-2">Enable in three steps</div>
          <ol className="text-sm text-[color:var(--atech-text-dim)] space-y-2 list-decimal list-inside leading-relaxed">
            <li>
              Create a free account at{" "}
              <a href="https://resend.com" target="_blank" rel="noreferrer" className="text-[color:var(--atech-copper)] hover:text-[color:var(--atech-copper-hover)]">resend.com</a>{" "}
              and verify a sender domain (you can use <span className="font-mono text-[color:var(--atech-text)]">onboarding@resend.dev</span> for testing first).
            </li>
            <li>
              Add these two lines to <span className="font-mono text-[color:var(--atech-text)]">/app/backend/.env</span>:
              <pre className="mt-2 bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] p-3 text-[11px] font-mono text-[color:var(--atech-text)] overflow-x-auto">
RESEND_API_KEY=re_xxxxxxxxxxxxxxxx
SENDER_EMAIL=hello@your-practice.co.uk</pre>
            </li>
            <li>
              Restart the backend: <span className="font-mono text-[color:var(--atech-text)]">sudo supervisorctl restart backend</span>.
              This card will flip to <span className="text-[#34D399]">Enabled</span> and every new invite will be emailed automatically.
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}


function StatusCaptionsEditor({ captions, onChange, onSave, busy, ok, err }) {
  return (
    <form onSubmit={onSave} className="space-y-4" data-testid="status-captions-form">
      <div className="card-surface p-8">
        <div className="label text-[color:var(--atech-copper)] mb-1">Timeline captions</div>
        <h2 className="font-display text-2xl text-[color:var(--atech-text)] mb-1">Status descriptions</h2>
        <p className="text-sm text-[color:var(--atech-text-dim)] mb-6 max-w-2xl">
          The one-sentence caption shown to clients beneath the active status on the
          project timeline. Leave blank to use the default wording.
        </p>

        <div className="space-y-5">
          {PROJECT_STATUSES.map((s) => (
            <div key={s.key}>
              <label className="label block mb-1.5">
                {s.label}
              </label>
              <textarea
                rows={2}
                value={captions?.[s.key] ?? ""}
                onChange={(e) => onChange((prev) => ({ ...prev, [s.key]: e.target.value }))}
                placeholder={STATUS_CAPTIONS[s.key]}
                className="w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3.5 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)] placeholder:text-[color:var(--atech-text-muted)] resize-none"
                data-testid={`caption-input-${s.key}`}
              />
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            type="submit"
            disabled={busy}
            className="btn-copper flex items-center gap-2 text-sm disabled:opacity-60"
            data-testid="save-captions-btn"
          >
            <Save size={14} />
            {busy ? "Saving…" : "Save captions"}
          </button>
          {ok && (
            <span className="flex items-center gap-1.5 text-xs text-[#34D399]" data-testid="captions-saved">
              <CheckCircle2 size={13} /> Saved
            </span>
          )}
          {err && (
            <span className="text-xs text-[#F87171]" data-testid="captions-error">{err}</span>
          )}
        </div>
      </div>
    </form>
  );
}
